// Tổng quan khu Quản trị NPP — lợi nhuận, đơn vị trực thuộc, vòng trạng thái đơn của khoảng đang chọn
// (components/overview/parts.tsx):
// - Lợi nhuận (01/11/2026, thay Doanh số): GET /finance/profit?from=&to= — tiền NPP giữ lại sau khi trừ phần thành
//   viên, Đại lý / CTV, chiết khấu, cước hãng; cùng công thức "Lợi nhuận" của trang Tài chính (theo ngày kết cục của
//   đơn). Chỉ người có quyền Tài chính; còn lại vẫn thấy Doanh số.
// - Doanh số, trạng thái đơn: GET /overview/orders?from=&to= (sổ tiền OrderFinance của NPP, không tính đơn hủy).
// - Đơn vị theo loại: GET /overview (chỉ khi có quyền xem đơn vị); khởi tạo NPP mới: /overview/setup.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Building2, CheckCircle2, ChevronDown, ChevronUp, Circle, ListChecks } from 'lucide-react'
import { adminGet } from '@/lib/adminApi'
import { canWith, useNppAdmin } from '@/lib/nppAdmin'
import { Section } from '@/components/ui/section'
import { countFormat } from '@/components/charts/chart-utils'
import { MoneyCard, OrdersCard, OverviewGrid, OverviewHead, RevenueHero, revenuePoints, rangePeriod, rangeQuery, useOverviewRange } from '@/components/overview/parts'
import { cn } from '@/lib/utils'
import { NPP_ROUTE_PERMISSIONS, toNpp } from '../nav'

interface Overview {
  shopsTotal: number
  network?: { agencies: number; ctvs: number; shops: number }
}

interface OverviewOrders {
  days: number
  daily: { date: string; ordersCount: number; gmv: number }[]
  /** Chỉ khi khoảng là một ngày: 24 giờ (giờ VN) */
  hourly?: { hour: number; ordersCount: number; gmv: number }[]
  summary: {
    totalOrders: number
    deliveredOrders: number
    deliveringOrders: number
    pendingOrders: number
    returnedOrders: number
    otherOrders: number
    networkGMV: number
  }
}

interface ProfitSeries {
  total: number
  daily: { date: string; profit: number }[]
  /** Chỉ khi khoảng là một ngày: 24 giờ (giờ VN) */
  hourly?: { hour: number; profit: number }[]
}

export default function NppOverviewPage() {
  const admin = useNppAdmin()
  const canUnits = canWith(admin, NPP_ROUTE_PERMISSIONS.units)
  const canFinance = canWith(admin, NPP_ROUTE_PERMISSIONS.finance)
  const [range, setRange] = useOverviewRange()
  const q = useQuery({ queryKey: ['npp', 'overview'], queryFn: () => adminGet<Overview>('/overview'), enabled: canUnits })
  const ord = useQuery({ queryKey: ['npp', 'overview', 'orders', range.from, range.to], queryFn: () => adminGet<OverviewOrders>(`/overview/orders?${rangeQuery(range)}`) })
  const profit = useQuery({
    queryKey: ['npp', 'overview', 'profit', range.from, range.to],
    queryFn: () => adminGet<ProfitSeries>(`/finance/profit?${rangeQuery(range)}`),
    enabled: canFinance,
  })
  const o = ord.data?.summary
  const net = q.data?.network
  const parts = [
    { label: 'Đại lý', value: net?.agencies ?? 0, to: '/npp/units?tenantType=agency' },
    { label: 'CTV', value: net?.ctvs ?? 0, to: '/npp/units?tenantType=ctv' },
    { label: 'Shop', value: net?.shops ?? 0, to: '/npp/units?tenantType=shop' },
  ]

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <OverviewHead range={range} onRange={setRange} />

      <SetupChecklist />

      <OverviewGrid
        revenue={
          canFinance ? (
            <RevenueHero
              label="Lợi nhuận gộp"
              total={profit.data?.total}
              points={revenuePoints(range, profit.data?.daily, profit.data?.hourly, (x) => x.profit, (h) => h.profit)}
              period={rangePeriod(range)}
              hours={range.from === range.last}
              loading={profit.isLoading}
              error={profit.isError}
              onRetry={() => profit.refetch()}
            />
          ) : (
            <RevenueHero
              label="COD dự kiến"
              total={o?.networkGMV}
              points={revenuePoints(range, ord.data?.daily, ord.data?.hourly, (x) => x.gmv, (h) => h.gmv)}
              period={rangePeriod(range)}
              hours={range.from === range.last}
              loading={ord.isLoading}
              error={ord.isError}
              onRetry={() => ord.refetch()}
            />
          )
        }
        units={
          canUnits ? (
            <MoneyCard
              tone="blue"
              icon={Building2}
              title="Thành viên"
              value={countFormat.format(net ? parts.reduce((sum, p) => sum + p.value, 0) : q.data?.shopsTotal ?? 0)}
              parts={parts}
              format={(v) => countFormat.format(v)}
              link={{ to: '/npp/units', label: 'Người dùng' }}
              loading={q.isLoading}
              error={q.isError}
            />
          ) : undefined
        }
        orders={<OrdersCard counts={o} loading={ord.isLoading} error={ord.isError} onRetry={() => ord.refetch()} />}
      />
    </div>
  )
}

// ─── Khởi tạo NPP (GET /overview/setup) ─────────────────────────
// NPP mới: thẻ đầy đủ 6 việc tới khi xong hết. NPP mặc định (Sunrise) đã chạy từ trước — không hiện.

interface SetupItem {
  key: string
  label: string
  done: boolean
  detail: string | null
  link: string
}

function SetupChecklist() {
  const q = useQuery({
    queryKey: ['npp', 'overview-setup'],
    queryFn: () => adminGet<{ npp: { displayName: string; isDefault: boolean }; items: SetupItem[]; done: boolean }>('/overview/setup'),
    staleTime: 0,
  })
  const [open, setOpen] = useState(true)
  const d = q.data
  if (!d || d.done || d.items.length === 0 || d.npp.isDefault) return null
  const doneCount = d.items.filter((i) => i.done).length
  const pct = Math.round((doneCount / d.items.length) * 100)

  return (
    <Section className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="motion-state flex min-h-11 w-full items-center gap-3 px-4 py-3 text-left hover:bg-paperSoft/60"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-accent/10 text-accent">
          <ListChecks className="size-4" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-ink">Khởi tạo nhà phân phối {d.npp.displayName}</span>
          <span className="block text-xs text-inkSoft">
            Đã xong <b className="tabular-nums text-ink">{doneCount}/{d.items.length}</b> việc
          </span>
        </span>
        <span className="hidden h-1.5 w-28 overflow-hidden rounded-full bg-paperSoft sm:block" aria-hidden>
          <span className="block h-full rounded-full bg-ok" style={{ width: `${pct}%` }} />
        </span>
        {open ? <ChevronUp className="size-4 shrink-0 text-muted" aria-hidden /> : <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden />}
      </button>
      {open && (
        <ol className="divide-y divide-rule border-t border-rule">
          {d.items.map((item, i) => (
            <li key={item.key} className="flex items-start gap-3 px-4 py-3">
              {item.done ? (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-ok" aria-label="Đã xong" />
              ) : (
                <Circle className="mt-0.5 size-4 shrink-0 text-muted" aria-label="Chưa xong" />
              )}
              <div className="min-w-0 flex-1">
                <p className={cn('text-sm', item.done ? 'text-inkSoft' : 'font-medium text-ink')}>
                  <span className="mr-1 tabular-nums text-muted">{i + 1}.</span>
                  {item.label}
                </p>
                {item.detail && <p className="text-xs text-muted">{item.detail}</p>}
              </div>
              <Link
                to={toNpp(item.link)}
                className={cn(
                  'motion-state -my-2.5 inline-flex min-h-11 shrink-0 items-center gap-1 py-2.5 text-xs font-medium hover:underline desktop:min-h-0',
                  item.done ? 'text-inkSoft' : 'text-accent',
                )}
              >
                {item.done ? 'Xem' : 'Làm ngay'}
                <ArrowRight className="size-3" aria-hidden />
              </Link>
            </li>
          ))}
        </ol>
      )}
    </Section>
  )
}
